import { useMemo, useRef, useState } from "react";
import * as R from "ramda";
import { bool, func, number, string } from "prop-types";

import { max } from "d3-array";
import { scaleLinear } from "d3-scale";
import parseISO from "date-fns/fp/parseISO";

import { lineDataShape } from "../../../../../Common/js/common/datacenterData.js";
import XAxisTrend from "../../../../../Common/js/components/Chart/XAxisTrend.jsx";
import YAxis from "../../../../../Common/js/components/Chart/YAxis.jsx";
import YearHoverLine from "../../../../../Common/js/components/Chart/YearHoverLine.jsx";
import TrendLine from "../../../../../Common/js/components/Chart/TrendLine.jsx";

import Popup from "../../../../../Common/js/components/Popup/Popup.jsx";

const propTypes = {
  excludeUrl: string,
  width: number,
  height: number,
  marginLeft: number.isRequired,
  lineData: lineDataShape.isRequired,
  xScale: func, // d3 scaleTime
  dottedLineForMissingData: bool,
};

const defaultProps = {
  height: 300,
  width: 800,
};

const MilestoneTrendChart = (props) => {
  const {
    excludeUrl,
    lineData,
    marginLeft,
    xScale,
    dottedLineForMissingData,
  } = props;

  const svgRef = useRef();

  const svgWidth = props.width;
  const svgHeight = props.height;

  // space for axis, etc
  const margin = {
    top: 80,
    right: 60,
    bottom: 40,
    left: marginLeft,
  };

  const width = svgWidth - margin.left - margin.right;
  const height = svgHeight - margin.top - margin.bottom;

  const [hoveredYear, setHoveredYear] = useState();
  const [popupInfo, setPopupInfo] = useState();

  const onYearHover = (year) => {
    setHoveredYear(year);
  };

  const hoveredDataPoint = R.find(
    R.propEq("timeFrame", hoveredYear),
    lineData.dataPoints,
  );

  /* y */

  const yScale = useMemo(() => {
    // always start the Y domain at 0
    const yDomain = [
      0,
      R.compose(max, R.pluck("value"), R.prop("dataPoints"))(lineData),
    ];

    return scaleLinear().range([height, 0]).domain(yDomain).nice();
  }, [height, lineData]);

  /* y-axis */

  return (
    <div className="milestone-trend-chart">
      <div
        className="milestone-trend-chart__popup-container"
        style={{
          position: "absolute",
          top: margin.top,
          left: margin.left,
          width: width,
          height: height,
          pointerEvents: "none",
        }}
      >
        <Popup
          isHidden={!popupInfo}
          offset={12}
          triangleSide="left"
          flipToContain=".narrative-chart"
          nudgeToContain=".narrative-chart"
          {...popupInfo}
        />
      </div>
      <svg width={svgWidth} height={svgHeight} ref={svgRef}>
        <XAxisTrend
          className="milestone-trend-chart__x-axis"
          height={height}
          margin={margin}
          scale={xScale}
        />
        <YAxis
          gridLines
          className="milestone-trend-chart__y-axis"
          margin={margin}
          width={width}
          height={height}
          scale={yScale}
          dataType={lineData.dataType}
        />

        <g
          className="milestone-trend-chart__chart-area"
          transform={`translate(${margin.left}, ${margin.top})`}
        >
          <rect
            className="milestone-trend-chart__chart-hit-area"
            x="0"
            y={-margin.top}
            width={width}
            height={height + margin.top + margin.bottom}
            style={{ fill: "red", opacity: 0 }}
          />

          <TrendLine
            className="milestone-trend-chart__trend-line"
            color="currentColor"
            dataPoints={lineData.dataPoints}
            height={height}
            xScale={xScale}
            yScale={yScale}
            dottedLineForMissingData={dottedLineForMissingData}
            onExcludedHover={setPopupInfo}
            excludeUrl={excludeUrl}
            // label={lineData.name}
          />

          {hoveredDataPoint && (
            <g
              className="milestone-chart__hovered-data-point"
              transform={
                "translate(" +
                (xScale(parseISO(hoveredYear)) - 8) +
                ", " +
                yScale(hoveredDataPoint.value) +
                ")"
              }
              textAnchor="end"
            >
              <text fill="white" stroke="white" strokeWidth="4px">
                {hoveredDataPoint.displayValue}
              </text>
              <text fill="currentColor">{hoveredDataPoint.displayValue}</text>
            </g>
          )}

          <YearHoverLine
            {...{ onYearHover, hoveredYear, xScale, svgRef, height, margin }}
          />
        </g>
      </svg>
    </div>
  );
};

MilestoneTrendChart.propTypes = propTypes;
MilestoneTrendChart.defaultProps = defaultProps;
export default MilestoneTrendChart;
